// React
import { useMemo, Fragment } from "react"

// Router
import { useNavigate } from "@/lib/router"
import { Routes } from "@/constants/routes"

// Dates
import { useLocalizedDateTime } from "@/lib/dates"

// Table
import {
	getCoreRowModel,
	useReactTable,
	createColumnHelper,
	flexRender,
} from "@/lib/table"

// UI
import { CardBody, CardWrapper } from "@/components/Card"
import { Heading } from "@/components/Typography"
import {
	Table,
	TableBody,
	TableDataCell,
	TableRowCell,
	TableHead,
	TableHeading,
} from "@/components/table-controls/TableItems"
import { Button } from "@/components/Button"

// Translations
import { useLang } from "@/context/useLang"
import { useTrans } from "@/lib/i18n"

// Data
import {
	METABASE_CROWDFUNDING_OVERVIEW_URL,
	endDateStatistics,
} from "@/constants/metabase"
import { useMetabasePublicQuestion } from "@/lib/metabase"

interface ProjectOverviewData {
	label: string
	value: string | number
}

interface CrowdfundingOverviewRow {
	nr: number
	metric: string
	value: number | null
}

type MetricFormat = "currency" | "number" | "percentage"

interface MetricDefinition {
	i18nKey: string
	format: MetricFormat
}

// Metabase rows are keyed by `nr` so a rename of the Dutch `metric` label
// upstream doesn't silently break a row. Unknown `nr` values are dropped with
// a console.warn (see the transform).
const METRIC_BY_NR: Record<number, MetricDefinition> = {
	2: {
		i18nKey: "dashboard:statistics.platform.total_raised",
		format: "currency",
	},
	3: {
		i18nKey: "dashboard:statistics.platform.percentage_projects_financed",
		format: "percentage",
	},
	4: {
		i18nKey: "dashboard:statistics.platform.total_projects_financed",
		format: "number",
	},
	5: {
		i18nKey: "dashboard:statistics.platform.total_projects_active",
		format: "number",
	},
	6: {
		i18nKey: "dashboard:statistics.platform.total_capital_repaid",
		format: "currency",
	},
	7: {
		i18nKey: "dashboard:statistics.platform.total_projects_repaid",
		format: "number",
	},
	8: {
		i18nKey:
			"dashboard:statistics.platform.total_projects_ended_partial_repaid",
		format: "number",
	},
	9: {
		i18nKey: "dashboard:statistics.platform.total_capital_loss",
		format: "currency",
	},
	10: {
		i18nKey: "dashboard:statistics.platform.total_interest_paid",
		format: "currency",
	},
	11: {
		i18nKey:
			"dashboard:statistics.platform.total_capital_and_interest_repaid",
		format: "currency",
	},
}

// Display order for the table. Rendering by this array rather than fetch order
// keeps the layout stable regardless of Metabase's row order. `nr` values are
// stable Metabase identifiers — never renumber.
const METRIC_ORDER = [
	2, // total_raised
	6, // total_capital_repaid
	10, // total_interest_paid
	11, // total_capital_and_interest_repaid
	9, // total_capital_loss
	4, // total_projects_financed
	5, // total_projects_active
	7, // total_projects_repaid
	8, // total_projects_ended_partial_repaid
	3, // percentage_projects_financed
]

/**
 * ProjectsOverviewTable
 * Displays static overview data for Zonhub Crowdfunded Projects, sourced from
 * the Metabase "crowdfunded projects overview" public question.
 */
export function ProjectsOverviewTable() {
	// Dates
	const DateTime = useLocalizedDateTime()

	// Router
	const navigate = useNavigate()

	// Translations
	const t = useTrans("dashboard")
	const { formatCurrency, formatNumber } = useLang()

	// Query
	const { rows, isLoading } =
		useMetabasePublicQuestion<CrowdfundingOverviewRow>(
			METABASE_CROWDFUNDING_OVERVIEW_URL,
		)

	// Transform Metabase rows to table format
	const tableData = useMemo<ProjectOverviewData[]>(() => {
		if (rows.length === 0) return []

		const byNr = new Map<number, number>()
		rows.forEach((row) => {
			const definition = METRIC_BY_NR[row.nr]
			if (!definition) {
				console.warn(
					`ProjectsOverviewTable: unknown nr ${row.nr} ("${row.metric}") from Metabase`,
				)
				return
			}
			byNr.set(row.nr, Number(row.value) || 0)
		})

		return METRIC_ORDER.filter((nr) => byNr.has(nr)).map((nr) => {
			const definition = METRIC_BY_NR[nr]
			const value = byNr.get(nr) ?? 0
			let formatted: string
			switch (definition.format) {
				case "currency":
					formatted = formatCurrency(value, {
						maximumFractionDigits: 0,
					})
					break
				case "percentage":
					formatted = `${formatNumber(value)}%`
					break
				case "number":
				default:
					formatted = formatNumber(value)
					break
			}
			return { label: t(definition.i18nKey), value: formatted }
		})
	}, [rows, t, formatCurrency, formatNumber])

	// Table setup
	const columnHelper = createColumnHelper<ProjectOverviewData>()
	const columns = useMemo(
		() => [
			columnHelper.accessor("label", {
				header: () => (
					<TableHeading variant="static">
						{t("dashboard:statistics.platform.metric")}
					</TableHeading>
				),
				cell: (info) => (
					<TableDataCell className="font-medium">
						{info.getValue()}
					</TableDataCell>
				),
			}),
			columnHelper.accessor("value", {
				header: () => (
					<TableHeading variant="static">
						{t("dashboard:statistics.platform.value")}
					</TableHeading>
				),
				cell: (info) => (
					<TableDataCell>{info.getValue()}</TableDataCell>
				),
			}),
		],
		[columnHelper, t],
	)

	const table = useReactTable({
		columns,
		data: tableData,
		getCoreRowModel: getCoreRowModel(),
	})

	return (
		<CardWrapper className="mb-4">
			<CardBody>
				<Heading as="h2" styleAs="h3" className="mb-6">
					{t("dashboard:statistics.platform.projects_overview_title")}
				</Heading>
				<p className="mb-4 text-sm text-gray-600">
					{t("dashboard:statistics.platform.as_per_date", {
						date: DateTime.fromJSDate(
							endDateStatistics,
						).toLocaleString(),
					})}
				</p>

				<Table>
					<TableHead>
						{table.getHeaderGroups().map((headerGroup) => (
							<TableRowCell
								key={headerGroup.id}
								withHover={false}
							>
								{headerGroup.headers.map((header) => (
									<Fragment key={header.id}>
										{flexRender(
											header.column.columnDef.header,
											header.getContext(),
										)}
									</Fragment>
								))}
							</TableRowCell>
						))}
					</TableHead>
					<TableBody>
						{isLoading
							? METRIC_ORDER.map((nr, index) => (
									<TableRowCell
										key={`loading-${nr}`}
										isOdd={index % 2 === 0}
										withHover={false}
									>
										<TableDataCell className="font-medium">
											<div className="h-4 w-48 animate-pulse rounded bg-gray-200" />
										</TableDataCell>
										<TableDataCell>
											<div className="h-4 w-24 animate-pulse rounded bg-gray-200" />
										</TableDataCell>
									</TableRowCell>
								))
							: table.getRowModel().rows.map((row, index) => (
									<TableRowCell
										key={row.id}
										isOdd={index % 2 === 0}
										withHover={false}
									>
										{row.getAllCells().map((cell) => (
											<Fragment key={cell.id}>
												{flexRender(
													cell.column.columnDef.cell,
													cell.getContext(),
												)}
											</Fragment>
										))}
									</TableRowCell>
								))}
					</TableBody>
				</Table>

				<div className="mt-4 flex flex-col items-center">
					<Button onClick={() => navigate(Routes.StatisticsProjects)}>
						{t(
							"dashboard:statistics.platform.view_project_details",
						)}
					</Button>
				</div>
			</CardBody>
		</CardWrapper>
	)
}
